/* globals window */

import React, { useEffect, useState } from 'react';
import {
  stroot,
  subscription as subscriptionAPI,
  organisation as orgAPI,
} from '@birdi/js-sdk';
import { css } from '@emotion/react';
import FreePlanSVG from '@birdi/icons/free-plan-2.svg';
import PlusPlanSVG from '@birdi/icons/plus-plan-2.svg';
import GrowthPlanSVG from '@birdi/icons/growth-plan-2.svg';
import EnterprisePlanSVG from '@birdi/icons/ultimate-plan-2.svg';
import { BirdiTheme, blocks, breakpoints } from '@birdi/theme';
import { useMetaSelector } from '../../meta/reducer';
import { CurrentPlan } from './types';
import { PlanChangedModal } from './change-plan/plan-changed-modal';
import { Link } from 'react-router';
import { format } from 'date-fns';
import { LoadingSpinner } from '@birdi/loading';
import { UsageSection } from '@birdi/usage-section';
import { SubscriptionPaymentModal } from '../billing/SubscriptionPaymentModal';

interface StorageData {
  mediaGb: number;
}

interface ProcessingData {
  processingGPUsage: number;
}

interface SeatsUsage {
  userSeatsUsed: number;
  guestSeatsUsed: number;
}

const getPlanIcon = (planName: string | undefined) => {
  if (planName?.toLowerCase()?.includes('free')) return <FreePlanSVG />;
  if (planName?.toLowerCase()?.includes('plus')) return <PlusPlanSVG />;
  if (planName?.toLowerCase()?.includes('growth')) return <GrowthPlanSVG />;
  if (planName?.toLowerCase()?.includes('ultimate'))
    return <EnterprisePlanSVG />;
  return <FreePlanSVG />;
};

export const PlanAndUsageV2 = (): React.ReactElement => {
  const [currentPlan, setCurrentPlan] = useState<CurrentPlan | null>(null);
  const [planChangedModal, setPlanChangedModal] = useState<boolean>(false);
  const [oldPlan, setOldPlan] = useState('');
  const [cancellingPlan, setCancellingPlan] = useState('no');
  const meta = useMetaSelector();

  const [storageData, setStorageData] = useState<StorageData | null>(null);
  const [processingData, setProcessingData] = useState<ProcessingData | null>(
    null,
  );
  const [workspacesUsed, setWorkspacesUsed] = useState<number | null>(null);
  const [seatsUsage, setSeatsUsage] = useState<SeatsUsage | null>(null);
  const [noStripeCustomerId, setNoStripeCustomerId] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadAllData = async () => {
      try {
        // Load all data concurrently
        const [
          storageRes,
          workspacesRes,
          seatsRes,
          processingRes,
          ...subscriptionResults
        ] = await Promise.all([
          orgAPI.getStorageInfo(stroot('faci1a')),
          orgAPI.getWorkspacesOptimized(stroot('wrki1a')),
          orgAPI.getSeatsCount(stroot('ussea1a')),
          orgAPI.getProcessingGPInfo(stroot('hi7ohd')),
          // Only load subscription data if needed
          ...(!!meta.organisation.primaryOrgId
            ? []
            : [
                subscriptionAPI
                  .getCurrentPlan(stroot('phahc4'))
                  .catch(() => ({ body: null })),
              ]),
        ]);

        // Set storage data
        setStorageData({
          mediaGb: parseFloat(
            parseFloat(
              String(storageRes.body?.totalUsedStorage || '0'),
            ).toFixed(1),
          ),
        });

        // Set workspaces data
        const workspaces = workspacesRes.body?.items;
        setWorkspacesUsed(Array.isArray(workspaces) ? workspaces.length : 0);

        // Set seats data
        setSeatsUsage({
          userSeatsUsed: Number(seatsRes.body?.userCount) || 0,
          guestSeatsUsed: Number(seatsRes.body?.guestCount) || 0,
        });

        // Set processing data
        setProcessingData(
          processingRes.body?.processingGPUsage
            ? {
                processingGPUsage:
                  Number(processingRes.body.processingGPUsage) || 0,
              }
            : null,
        );
        const subscriptionRes = subscriptionResults[0];
        if (
          subscriptionRes &&
          'body' in subscriptionRes &&
          subscriptionRes.body
        ) {
          setCurrentPlan(subscriptionRes.body);
        } else {
          setNoStripeCustomerId(true);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setNoStripeCustomerId(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadAllData();
  }, [meta.organisation.primaryOrgId]);

  // Show loading spinner while data is being fetched
  if (isLoading) {
    return (
      <div
        css={css`
          padding: 2em;
        `}
      >
        <LoadingSpinner color="#2287FA" size="1.5em" />
        <SubscriptionPaymentModal />
      </div>
    );
  }

  const sanitisePlanTerm = (priceDescription: string) => {
    const lowerDescription = priceDescription?.toLowerCase();
    if (lowerDescription?.includes('monthly')) {
      return 'Monthly';
    } else {
      return 'Annual';
    }
  };
  return (
    <div>
      <SubscriptionPaymentModal />
      <h2
        css={css`
          margin: 1em;
        `}
      >
        Plan & usage
      </h2>
      <div css={blocks.pageGraySection}>
        <p>Manage your plan and usage.</p>
      </div>

      {noStripeCustomerId && (
        <div
          css={css`
            padding: 0 1em;
          `}
        >
          <p>No Stripe customer ID found. Please contact support.</p>
        </div>
      )}
      <div
        css={css`
          padding: 0 1em;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            gap: 1em;
          `}
        >
          <h3>Subscription Plan</h3>
          <div
            css={css`
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: flex-start;
              gap: 1em;
            `}
          >
            <Link
              to="/billing/options"
              css={(theme) => css`
                padding: 0.5em;
                background-color: ${theme.lightOcean};
                border-radius: 5px;
                span {
                  color: ${theme.deepOcean};
                  text-decoration: underline;
                }
              `}
            >
              <span>Manage plan</span>
            </Link>
            <div
              css={css`
                display: flex;
                flex-direction: row;
                align-items: flex-start;
                justify-content: flex-start;
                gap: 0.5em;
              `}
            >
              {currentPlan &&
                currentPlan.trialEnd &&
                currentPlan.trialDaysRemaining &&
                currentPlan.trialDaysRemaining > 0 && (
                  <p
                    css={(theme: BirdiTheme) => css`
                      background-color: ${theme.sand};
                      border-radius: 5px;
                      padding: 0.5em;
                      margin-bottom: 0.5em;
                    `}
                  >
                    {`Trial ends: ${format(currentPlan.trialEnd * 1000, 'dd MMM yyyy')}`}
                  </p>
                )}
              {currentPlan &&
                !currentPlan.basePlan.stripeProductName
                  .toLowerCase()
                  .includes('free') &&
                !currentPlan.nextPhaseStartDate &&
                currentPlan.status !== 'canceled' &&
                !currentPlan.cancelDate && (
                  <p
                    css={(theme: BirdiTheme) => css`
                      background-color: ${theme.sand};
                      border-radius: 5px;
                      padding: 0.5em;
                    `}
                  >
                    {`Subscription renewal date: ${format(currentPlan.currentPeriodEnd * 1000, 'dd MMM yyyy')}`}
                  </p>
                )}
              {currentPlan &&
                currentPlan.status === 'canceled' &&
                !currentPlan.nextPhaseStartDate &&
                !currentPlan.cancelDate && (
                  <p>
                    {`Your subscription has been cancelled and is active until ${format(currentPlan.currentPeriodEnd * 1000, 'dd MMM yyyy')}`}
                  </p>
                )}
              {currentPlan &&
                currentPlan.nextPhaseStartDate &&
                !currentPlan.cancelDate && (
                  <p>
                    {`Plan scheduled to update to ${currentPlan.nextPhaseBasePlan.stripeProductName} on ${format(currentPlan.nextPhaseStartDate * 1000, 'dd MMM yyyy')}`}
                  </p>
                )}
              {currentPlan && currentPlan.cancelDate && (
                <p>
                  {`Your current subscription is scheduled to end ${format(currentPlan.cancelDate * 1000, 'dd MMM yyyy')}`}
                </p>
              )}
            </div>
          </div>
        </div>

        <div
          css={(theme) => css`
            display: flex;
            flex-direction: row;
            border: solid 1px ${theme.mono30};
            border-radius: 8px;
            padding: 0.5em 1em;
            max-width: 35em;
            width: 100%;
            @media (max-width: ${breakpoints.xs}) {
              width: calc(100% - 2em);
            }
          `}
        >
          <div
            css={css`
              vertical-align: middle;
              margin: 1em 2em 1em 0; // 2em 2em 2em 0;
              @media (max-width: ${breakpoints.xs}) {
                display: none;
                margin: 0;
              }
              svg {
                font-size: 7em;
              }
            `}
          >
            {getPlanIcon(currentPlan?.basePlan?.stripeProductName)}
          </div>
          <table
            css={css`
              width: 100%;
            `}
          >
            <tr>
              <td>
                <span
                  css={css`
                    font-weight: bold;
                  `}
                >
                  {currentPlan?.basePlan?.stripeProductName}
                </span>
              </td>
              <td>
                <span
                  css={css`
                    font-weight: bold;
                  `}
                >
                  {sanitisePlanTerm(
                    currentPlan?.basePlan?.stripePriceDescription || '',
                  )}
                </span>
              </td>
            </tr>
            <tr>
              <td>User Seats</td>
              <td>
                {currentPlan?.basePlan?.userLimitSeats ?? 'Unlimited'} Included
              </td>
            </tr>
            <tr>
              <td>Guest Seats</td>
              <td>
                {currentPlan?.basePlan?.guestsLimitSeats ?? 'Unlimited'}{' '}
                Included
              </td>
            </tr>
            <tr>
              <td>Workspaces</td>
              <td>{currentPlan?.basePlan?.workspacesLimit} Included</td>
            </tr>
            <tr>
              <td>Processing</td>
              <td>
                {meta.organisation.processingFlavour === 'ops'
                  ? 'Ops'
                  : (() => {
                      const planGP =
                        currentPlan?.basePlan?.processingTermLimitGigapixels ??
                        0;
                      const isInTrial =
                        currentPlan?.trialEnd &&
                        currentPlan?.trialDaysRemaining &&
                        currentPlan.trialDaysRemaining > 0;
                      const trialLimit = 10;
                      const showTrialLimit = isInTrial && planGP > trialLimit;
                      const effectiveLimit = showTrialLimit
                        ? trialLimit
                        : planGP;
                      const period =
                        sanitisePlanTerm(
                          currentPlan?.basePlan?.stripePriceDescription || '',
                        ).toLowerCase() === 'monthly'
                          ? 'per month'
                          : 'per year';

                      return showTrialLimit
                        ? `${effectiveLimit} GP Credit during trial (${planGP} GP after trial ${period})`
                        : `${effectiveLimit} GP Credit ${period}`;
                    })()}
              </td>
            </tr>
            <tr>
              <td>Storage</td>
              <td>
                {currentPlan?.basePlan?.storageLimitGigabytes} GB Data Included
              </td>
            </tr>
          </table>
        </div>

        <div
          css={css`
            margin-top: 1em;
            border-radius: 8px;
            max-width: 37em;
            width: 100%;
            @media (max-width: ${breakpoints.xs}) {
              width: calc(100% - 2em);
            }
            > div > div {
              display: grid;
              grid-template-columns: 1fr;
              gap: 1.5em;
            }
          `}
        >
          <UsageSection
            seatsCurrent={seatsUsage?.userSeatsUsed || 0}
            seatsTotal={currentPlan?.basePlan?.userLimitSeats || 0}
            guestsCurrent={seatsUsage?.guestSeatsUsed || 0}
            guestsTotal={currentPlan?.basePlan?.guestsLimitSeats || 'Unlimited'}
            processingUsed={processingData?.processingGPUsage || 0}
            processingTotal={
              currentPlan?.basePlan?.processingTermLimitGigapixels || 0
            }
            storageUsed={storageData?.mediaGb || 0}
            storageTotal={currentPlan?.basePlan?.storageLimitGigabytes || 0}
            workspacesUsed={workspacesUsed || 0}
            workspacesTotal={currentPlan?.basePlan?.workspacesLimit || 0}
            whiteBorder={false}
            interval={
              sanitisePlanTerm(
                currentPlan?.basePlan?.stripePriceDescription || '',
              ).toLowerCase() === 'monthly'
                ? 'month'
                : 'year'
            }
          />
        </div>
      </div>

      {planChangedModal && oldPlan && (
        <PlanChangedModal
          open={planChangedModal}
          close={() => setPlanChangedModal(false)}
          oldPlan={oldPlan}
          newPlan={(meta.organisation as any).plan}
          cancelPlan={!!(cancellingPlan === 'yes')}
        />
      )}
    </div>
  );
};
